/**
 * CampaignsListPage - Admin email campaign management
 *
 * 4-tab layout:
 *   Queue          – campaign chains grouped by tour stop (cards)
 *   Scheduled Sends – chronological list of upcoming sends
 *   Sent           – campaigns with status = sent
 *   All Campaigns  – full list with status filter (legacy view)
 */

import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authenticatedFetch } from '../../../api/auth';
import {
  getCampaignQueue,
  getCampaignQueueAudience,
  getScheduledSends,
  cancelCampaign,
  getCampaignQuota,
  approveChain,
  dismissChain,
  addReminderToChain,
  deleteCampaign,
  updateCampaign,
  type CampaignQuota,
  type CampaignChainTourStop,
  type QueuedCampaign,
  type ScheduledSend,
  type CampaignQueueResponse,
  type ScheduledSendsResponse,
} from '../../../api/admin';
import CampaignEmailPreview from '../../../components/campaigns/CampaignEmailPreview';
import RecipientsModal from '../../../components/campaigns/RecipientsModal';
import TemplatesTab from './TemplatesTab';
import InlineDateEditor from './InlineDateEditor';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import Tooltip from '../../../components/ui/Tooltip';
import { naiveDatetimeToUTC, todayInTimezone } from '../../../utils/timezone';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface LegacyCampaign {
  id: string;
  name: string;
  subject: string;
  campaign_type: string;
  status: string;
  scheduled_for: string | null;
  scheduled_timezone: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  sent_count: number;
  tour_stop_id: string | null;
  sequence_number: number;
  parent_campaign_id: string | null;
  created_by?: { first_name: string; last_name: string } | null;
  email_sends?: { count: number }[];
  /** SDA-267: per-campaign email stats (webhook-filled timestamps); null until events arrive. */
  email_stats?: { total: number; delivered: number; opened: number; clicked: number; bounced: number } | null;
}

type Tab = 'queue' | 'scheduled' | 'sent' | 'all' | 'templates';
type CampaignStatus = 'all' | 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'pending_approval';
// SDA-193: Queue tab sort + type-filter controls
type QueueSort = 'soonest' | 'tour' | 'show' | 'type';
type QueueTypeFilter = 'all' | 'tour_announcement' | 'reminder';

const TZ_ABBR: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
};

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

function getStatusBadge(status: string) {
  const styles: Record<string, string> = {
    draft: 'bg-warm-100 text-warm-600',
    scheduled: 'bg-primary-100 text-primary-700',
    pending_approval: 'bg-amber-100 text-amber-700',
    sending: 'bg-warning-100 text-warning-700',
    sent: 'bg-success-100 text-success-700',
    cancelled: 'bg-danger-100 text-danger-700',
  };
  const labels: Record<string, string> = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    pending_approval: 'Pending Approval',
    sending: 'Sending',
    sent: 'Sent',
    cancelled: 'Cancelled',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
      {labels[status] || status}
    </span>
  );
}

function getTypeBadge(type: string) {
  const styles: Record<string, string> = {
    tour_announcement: 'bg-purple-100 text-purple-700',
    reminder: 'bg-blue-100 text-blue-700',
    general: 'bg-warm-100 text-warm-600',
    seasonal: 'bg-primary-100 text-primary-700',
  };
  const labels: Record<string, string> = {
    tour_announcement: 'Tour Announcement',
    reminder: 'Reminder',
    general: 'General',
    seasonal: 'Seasonal',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[type] || 'bg-warm-100 text-warm-600'}`}>
      {labels[type] || type}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** SDA-245: format a date-only estimate string (YYYY-MM-DD). Parsed as local
 *  noon so the calendar date never shifts; year shown only when it differs
 *  from the current year. */
function formatEstDate(date: string) {
  const dt = new Date(`${date}T12:00:00`);
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(dt.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  });
}

function formatDateTime(date: string, timezone?: string | null) {
  return new Date(date).toLocaleString('en-US', {
    timeZone: timezone || undefined,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatShortDate(date: string) {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMonthYear(date: string) {
  return new Date(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Queue chain helpers (SDA-193: sort/filter + "next due" date)
// ---------------------------------------------------------------------------

// A chain's "type" is its sequence-1 campaign type — reminder-led chains start
// with a reminder, announcement-led chains start with a tour_announcement. Falls
// back to the first campaign if no sequence_number === 1 row is present.
function getChainType(chain: CampaignChainTourStop): string {
  const seqOne = chain.campaigns.find((c) => c.sequence_number === 1);
  return (seqOne ?? chain.campaigns[0])?.campaign_type ?? '';
}

// Earliest scheduled_for among a chain's campaigns — the date it's next "due" to
// send. Returns null when no campaign in the chain has a scheduled date.
function getNextSendAt(chain: CampaignChainTourStop): string | null {
  let earliest: string | null = null;
  for (const c of chain.campaigns) {
    if (!c.scheduled_for) continue;
    if (earliest === null || c.scheduled_for < earliest) earliest = c.scheduled_for;
  }
  return earliest;
}

// SDA-222: date-level "past" check that mirrors the backend chain-approve guard
// EXACTLY (admin.ts ~9993: date component of scheduled_for < today, both UTC),
// so the UI's notion of "overdue" agrees with what actually blocks Approve All &
// Schedule. A same-day, earlier-clock-time campaign is NOT overdue here — approve
// compares calendar dates, not full timestamps. Exported for unit tests.
export function isBlockingOverdue(scheduledFor: string | null): boolean {
  if (!scheduledFor) return false;
  const todayUtc = new Date().toISOString().split('T')[0]!;
  return scheduledFor.split('T')[0]! < todayUtc;
}

// A chain blocks Approve All & Schedule iff any of its still-pending campaigns is
// past-dated — exactly the set the backend guard checks (it filters to `pending`
// then runs the date compare). Exported for unit tests.
export function chainHasOverdue(chain: CampaignChainTourStop): boolean {
  return chain.campaigns.some(
    (c) => c.status === 'pending_approval' && isBlockingOverdue(c.scheduled_for),
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CampaignsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Queue state
  const [queueData, setQueueData] = useState<CampaignQueueResponse | null>(null);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);
  // SDA-187: action (approve/dismiss/delete) failures use a SEPARATE, dismissible
  // error so they don't replace the whole queue list the way a load error does.
  const [queueActionError, setQueueActionError] = useState<string | null>(null);
  // SDA-222: transient success banner (e.g. after an inline re-date) so a
  // successful reschedule is explicitly confirmed, not just implied by the row.
  const [queueActionSuccess, setQueueActionSuccess] = useState<string | null>(null);

  // Scheduled sends state
  const [scheduledData, setScheduledData] = useState<ScheduledSendsResponse | null>(null);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduledError, setScheduledError] = useState<string | null>(null);

  // Legacy list state (sent + all tabs)
  const [campaigns, setCampaigns] = useState<LegacyCampaign[]>([]);
  const [legacyTotal, setLegacyTotal] = useState(0);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyError, setLegacyError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CampaignStatus>(
    (searchParams.get('status') as CampaignStatus) || 'all'
  );
  const [page, setPage] = useState(1);

  // Active tab -- initialised after queue loads
  const [activeTab, setActiveTab] = useState<Tab | null>(null);

  // UI state
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [previewCampaign, setPreviewCampaign] = useState<QueuedCampaign | null>(null);
  const [recipientsModal, setRecipientsModal] = useState<{ campaignId: string; campaignName: string } | null>(null);
  const [addReminderForChain, setAddReminderForChain] = useState<string | null>(null);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('10:00');
  const [reminderTimezone, setReminderTimezone] = useState('America/New_York');
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    variant: 'danger' | 'primary';
    action: () => Promise<void> | void;
  } | null>(null);
  const [reminderError, setReminderError] = useState<string | null>(null);

  // Inline date editing state (Queue tab)
  const [editingDateCampaignId, setEditingDateCampaignId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editTimezone, setEditTimezone] = useState('America/New_York');
  const [editDateSaving, setEditDateSaving] = useState(false);
  const [editDateError, setEditDateError] = useState<string | null>(null);

  // Date range filter state (All Campaigns tab)
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Quota state
  const [quota, setQuota] = useState<CampaignQuota | null>(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  // In-flight guards prevent React.StrictMode dev double-mount from firing
  // these expensive endpoints twice per visit.
  const queueLoadInFlightRef = useRef(false);
  const audienceLoadInFlightRef = useRef(false);
  const quotaLoadInFlightRef = useRef(false);

  const loadQueue = useCallback(async () => {
    if (queueLoadInFlightRef.current) return;
    queueLoadInFlightRef.current = true;
    try {
      setQueueLoading(true);
      setQueueError(null);
      const result = await getCampaignQueue();
      setQueueData(result);
    } catch (err) {
      setQueueError(err instanceof Error ? err.message : 'Failed to load queue');
    } finally {
      setQueueLoading(false);
      queueLoadInFlightRef.current = false;
    }
  }, []);

  const loadQueueAudience = useCallback(async () => {
    if (audienceLoadInFlightRef.current) return;
    audienceLoadInFlightRef.current = true;
    try {
      const audienceMap = await getCampaignQueueAudience();
      // Return the SAME reference when nothing actually changed (empty map, or
      // entries match existing state). Otherwise the [queueData] hydration
      // effect re-fires on every audience response and risks an infinite loop.
      setQueueData((prev) => {
        if (!prev) return prev;
        let didUpdate = false;
        const newChains = prev.chains.map((chain) => {
          const entry = audienceMap[chain.tour_stop_id];
          if (!entry) return chain;
          const first = chain.campaigns.find((c) => c.sequence_number === 1);
          if (first && first.estimated_audience === entry.estimated_audience) {
            return chain;
          }
          didUpdate = true;
          return {
            ...chain,
            campaigns: chain.campaigns.map((c) =>
              c.sequence_number === 1
                ? { ...c, estimated_audience: entry.estimated_audience }
                : c,
            ),
          };
        });
        if (!didUpdate) return prev;
        return { ...prev, chains: newChains };
      });
    } catch (err) {
      // Audience hydration failure shouldn't blow up the page — chains stay
      // usable, audience cells just remain in their loading state.
      console.error('Failed to load queue audience:', err);
    } finally {
      audienceLoadInFlightRef.current = false;
    }
  }, []);

  const loadScheduled = useCallback(async () => {
    try {
      setScheduledLoading(true);
      setScheduledError(null);
      const result = await getScheduledSends();
      setScheduledData(result);
    } catch (err) {
      setScheduledError(err instanceof Error ? err.message : 'Failed to load scheduled sends');
    } finally {
      setScheduledLoading(false);
    }
  }, []);

  const loadLegacy = useCallback(async (status: CampaignStatus, pg: number, fromDate?: string, toDate?: string) => {
    try {
      setLegacyLoading(true);
      setLegacyError(null);
      const params = new URLSearchParams();
      if (status !== 'all') params.append('status', status);
      if (fromDate) params.append('date_from', fromDate);
      if (toDate) params.append('date_to', toDate);
      params.append('page', String(pg));
      params.append('per_page', String(PAGE_SIZE));

      const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns?${params}`);
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch campaigns');
      }
      setCampaigns(result.data || []);
      setLegacyTotal(result.pagination?.total || 0);
    } catch (err) {
      setLegacyError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLegacyLoading(false);
    }
  }, []);

  // Load queue on mount, set default tab
  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Hydrate audience after a chain-list load completes. Triggered each time
  // queueData is replaced (initial load, post-approve, post-dismiss) by
  // watching for fresh chains whose audience hasn't been computed yet.
  useEffect(() => {
    if (!queueData?.chains.length) return;
    const needsAudience = queueData.chains.some((chain) => {
      const first = chain.campaigns.find((c) => c.sequence_number === 1);
      return first && first.estimated_audience == null;
    });
    if (!needsAudience) return;
    loadQueueAudience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueData]);

  // Load quota (refresh when switching tabs). Ref guard prevents StrictMode
  // dev double-mount duplicate calls.
  useEffect(() => {
    if (quotaLoadInFlightRef.current) return;
    quotaLoadInFlightRef.current = true;
    getCampaignQuota()
      .then(setQuota)
      .catch(console.error)
      .finally(() => {
        quotaLoadInFlightRef.current = false;
      });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== null || queueData === null) return;
    const urlTab = searchParams.get('tab') as Tab | null;
    if (urlTab && ['queue', 'scheduled', 'sent', 'all', 'templates'].includes(urlTab)) {
      setActiveTab(urlTab);
    } else {
      setActiveTab(queueData.total_pending > 0 ? 'queue' : 'scheduled');
    }
  }, [queueData, activeTab, searchParams]);

  // Lazy-load data when switching tabs
  useEffect(() => {
    if (activeTab === null) return;
    const params = new URLSearchParams();
    params.set('tab', activeTab);
    if (activeTab === 'all' && statusFilter !== 'all') params.set('status', statusFilter);
    setSearchParams(params, { replace: true });

    if (activeTab === 'scheduled' && !scheduledData) {
      loadScheduled();
    }
    if (activeTab === 'sent') {
      loadLegacy('sent', 1);
    }
    if (activeTab === 'all') {
      loadLegacy(statusFilter, page, dateFrom, dateTo);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'all') {
      loadLegacy(statusFilter, page, dateFrom, dateTo);
      const params = new URLSearchParams();
      params.set('tab', 'all');
      if (statusFilter !== 'all') params.set('status', statusFilter);
      setSearchParams(params, { replace: true });
    }
  }, [statusFilter, page, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Queue actions
  // -------------------------------------------------------------------------

  const handleApproveChain = (tourStopId: string) => {
    setConfirmAction({
      title: 'Approve Chain',
      message: 'Approve all campaigns in this chain and schedule them for sending?',
      confirmLabel: 'Approve All',
      variant: 'primary',
      action: async () => {
        setQueueActionError(null);
        setQueueActionSuccess(null);
        try {
          setActionLoading(tourStopId);
          await approveChain(tourStopId);
          await loadQueue();
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to approve chain';
          setQueueActionError(message);
          // SDA-222: the backend blocks the whole chain when ANY pending campaign
          // is past-dated (INVALID_DATE — message contains "scheduled in the
          // past"). Funnel the user straight onto the exact blocking campaign:
          // open its inline date editor and scroll it into view so they fix the
          // right one instead of guessing. Do this BEFORE loadQueue so the chain
          // is still in state to look up.
          if (message.includes('scheduled in the past')) {
            const chain = queueData?.chains.find((c) => c.tour_stop_id === tourStopId);
            const blocking = chain?.campaigns.find(
              (c) => c.status === 'pending_approval' && isBlockingOverdue(c.scheduled_for),
            );
            if (blocking) {
              handleStartEditDate(blocking);
              setTimeout(() => {
                document
                  .getElementById(`campaign-row-${blocking.id}`)
                  ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
              }, 100);
            }
          }
          await loadQueue(); // refresh so a stale/already-handled chain drops away
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleDismissChain = (tourStopId: string) => {
    setConfirmAction({
      title: 'Dismiss Chain',
      message: 'Dismiss all campaigns in this chain? This cannot be undone.',
      confirmLabel: 'Dismiss',
      variant: 'danger',
      action: async () => {
        setQueueActionError(null);
        setQueueActionSuccess(null);
        try {
          setActionLoading(tourStopId);
          await dismissChain(tourStopId);
          await loadQueue();
        } catch (err) {
          setQueueActionError(err instanceof Error ? err.message : 'Failed to dismiss chain');
          await loadQueue(); // refresh so a stale/already-handled chain drops away
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleAddReminder = async (tourStopId: string) => {
    if (!reminderDate) return;
    // SDA-268: Eastern today, matching the add-reminder API's own guard. A UTC
    // slice refused a valid same-day reminder from 8 PM ET onward.
    const today = todayInTimezone();
    if (reminderDate < today) {
      setReminderError('Date must be today or later');
      return;
    }
    setReminderError(null);
    const scheduled_for = `${reminderDate}T${reminderTime}:00`;
    try {
      setActionLoading(`add-reminder-${tourStopId}`);
      await addReminderToChain(tourStopId, {
        scheduled_for,
        scheduled_timezone: reminderTimezone,
      });
      setAddReminderForChain(null);
      setReminderDate('');
      setReminderTime('10:00');
      await loadQueue();
    } catch (err) {
      setReminderError(err instanceof Error ? err.message : 'Failed to add reminder');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCampaign = (campaignId: string) => {
    setConfirmAction({
      title: 'Remove Reminder',
      message: 'Remove this reminder from the chain?',
      confirmLabel: 'Remove',
      variant: 'danger',
      action: async () => {
        setQueueActionError(null);
        setQueueActionSuccess(null);
        try {
          await deleteCampaign(campaignId);
          await loadQueue();
        } catch (err) {
          setQueueActionError(err instanceof Error ? err.message : 'Failed to remove reminder');
          await loadQueue(); // refresh so a stale chain/reminder drops away
        }
      },
    });
  };

  const handlePreview = (campaign: QueuedCampaign) => {
    // SDA-192: CampaignEmailPreview fetches the faithful render itself (variables
    // resolved + footer) from /render-preview. Just open the modal.
    setPreviewCampaign(campaign);
  };

  // -------------------------------------------------------------------------
  // Inline date editing (Queue tab)
  // -------------------------------------------------------------------------

  const handleStartEditDate = (campaign: { id: string; scheduled_for: string | null; scheduled_timezone: string | null }) => {
    if (campaign.scheduled_for) {
      const tz = campaign.scheduled_timezone || 'America/New_York';
      const dt = new Date(campaign.scheduled_for);
      const parts = dt.toLocaleString('en-CA', { timeZone: tz, hour12: false }).split(', ');
      const datePart = parts[0] || '';
      const timePart = parts[1]?.slice(0, 5) || '10:00';
      setEditDate(datePart);
      setEditTime(timePart);
      setEditTimezone(tz);
    } else {
      setEditDate('');
      setEditTime('10:00');
      setEditTimezone('America/New_York');
    }
    setEditDateError(null);
    setQueueActionSuccess(null);
    setEditingDateCampaignId(campaign.id);
  };

  const handleCancelEditDate = () => {
    setEditingDateCampaignId(null);
    setEditDateError(null);
  };

  const handleSaveEditDate = async (campaignId: string) => {
    if (!editDate) {
      setEditDateError('Date is required');
      return;
    }
    const today = new Date().toISOString().split('T')[0] ?? '';
    if (editDate < today) {
      setEditDateError('Date must be today or later');
      return;
    }
    try {
      setEditDateSaving(true);
      setEditDateError(null);
      // Convert the entered wall-clock time in the selected timezone to a UTC
      // instant (reuses the same helper as the campaign form). Appending 'Z'
      // here would wrongly treat the local time as UTC.
      const scheduled_for = naiveDatetimeToUTC(`${editDate}T${editTime}`, editTimezone);
      await updateCampaign(campaignId, {
        scheduled_for,
        scheduled_timezone: editTimezone,
      });
      setEditingDateCampaignId(null);
      if (activeTab === 'scheduled') {
        // Scheduled Sends tab owns its own dataset — refetch it.
        await loadScheduled();
      } else {
        // SDA-222: confirm the reschedule explicitly (the visible date change +
        // "Overdue" pill clearing is the primary signal; this makes it loud).
        setQueueActionError(null);
        setQueueActionSuccess('Send date updated.');
        // Queue tab: optimistic patch the campaign in local state (no full reload)
        setQueueData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            chains: prev.chains.map((chain) => ({
              ...chain,
              campaigns: chain.campaigns.map((c) =>
                c.id === campaignId
                  ? { ...c, scheduled_for, scheduled_timezone: editTimezone }
                  : c
              ),
            })),
          };
        });
      }
    } catch (err) {
      setEditDateError(err instanceof Error ? err.message : 'Failed to update date');
    } finally {
      setEditDateSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Tab bar
  // -------------------------------------------------------------------------

  const queueCount = queueData?.total_pending ?? 0;

  const tabs: { key: Tab; label: string; badge?: number }[] = [
    { key: 'queue', label: 'Queue', badge: queueCount > 0 ? queueCount : undefined },
    { key: 'scheduled', label: 'Scheduled Sends' },
    { key: 'sent', label: 'Sent' },
    { key: 'all', label: 'All Campaigns' },
    { key: 'templates', label: 'Email Templates' },
  ];

  // -------------------------------------------------------------------------
  // Loading skeleton while queue loads (determines default tab)
  // -------------------------------------------------------------------------

  if (activeTab === null) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(legacyTotal / PAGE_SIZE);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Campaigns</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Create and manage email campaigns for studios.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/campaigns/interest"
              className="px-4 py-2 min-h-[44px] flex items-center border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors text-sm font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Interest List
            </Link>
            <Link to="/admin/campaigns/new" className="btn-primary min-h-[44px] flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">New </span>Campaign
            </Link>
          </div>
          {quota && (() => {
            const pct = quota.daily_limit > 0 ? (quota.quota_used / quota.daily_limit) * 100 : 0;
            const colorClass = pct > 85 ? 'text-danger-600 bg-danger-50 border-danger-200' :
              pct > 60 ? 'text-warning-600 bg-warning-50 border-warning-200' : 'text-success-600 bg-success-50 border-success-200';
            const barColor = pct > 85 ? 'bg-danger-500' : pct > 60 ? 'bg-warning-500' : 'bg-success-500';
            return (
              <div className={`hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${colorClass}`}>
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div className="w-16 h-1.5 bg-white/50 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <span>{quota.quota_used.toLocaleString()} / {quota.daily_limit.toLocaleString()} sent (24h)</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Approved campaigns parked behind the daily send limit (warm-up) */}
      {quota && quota.awaiting_send_count > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 p-4 text-sm mb-6">
          <span>
            <strong>{quota.awaiting_send_count} approved campaign{quota.awaiting_send_count !== 1 ? 's' : ''}</strong>
            {' '}{quota.awaiting_send_count !== 1 ? 'are' : 'is'} waiting on the daily send limit ({quota.daily_limit.toLocaleString()}/24h).
            They send automatically as quota frees up.
          </span>
          {activeTab !== 'scheduled' && (
            <button
              onClick={() => {
                setActiveTab('scheduled');
                setPage(1);
              }}
              className="font-semibold underline hover:text-amber-900"
            >
              View them
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-warm-100 p-1 rounded-lg w-full sm:w-fit overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              setPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap min-h-[44px] ${
              activeTab === tab.key
                ? 'bg-white text-warm-900 shadow-sm'
                : 'text-warm-500 hover:text-warm-700'
            }`}
          >
            {tab.label}
            {tab.badge !== undefined && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'queue' && (
        <>
        <QueueTab
          data={queueData}
          loading={queueLoading}
          error={queueError}
          actionError={queueActionError}
          onDismissActionError={() => setQueueActionError(null)}
          actionSuccess={queueActionSuccess}
          onDismissActionSuccess={() => setQueueActionSuccess(null)}
          actionLoading={actionLoading}
          addReminderForChain={addReminderForChain}
          setAddReminderForChain={setAddReminderForChain}
          reminderDate={reminderDate}
          setReminderDate={setReminderDate}
          reminderTime={reminderTime}
          setReminderTime={setReminderTime}
          reminderTimezone={reminderTimezone}
          setReminderTimezone={setReminderTimezone}
          reminderError={reminderError}
          setReminderError={setReminderError}
          onApproveChain={handleApproveChain}
          onDismissChain={handleDismissChain}
          onAddReminder={handleAddReminder}
          onPreview={handlePreview}
          onDeleteCampaign={handleDeleteCampaign}
          onViewRecipients={(campaignId, campaignName) => setRecipientsModal({ campaignId, campaignName })}
          navigate={navigate}
          editingDateCampaignId={editingDateCampaignId}
          editDate={editDate}
          editTime={editTime}
          editTimezone={editTimezone}
          editDateSaving={editDateSaving}
          editDateError={editDateError}
          onStartEditDate={handleStartEditDate}
          onCancelEditDate={handleCancelEditDate}
          onSaveEditDate={handleSaveEditDate}
          onEditDateChange={setEditDate}
          onEditTimeChange={setEditTime}
          onEditTimezoneChange={setEditTimezone}
        />
        </>
      )}

      {activeTab === 'scheduled' && (
        <ScheduledTab
          data={scheduledData}
          loading={scheduledLoading}
          error={scheduledError}
          onViewRecipients={(campaignId, campaignName) => setRecipientsModal({ campaignId, campaignName })}
          onCancel={async (campaignId) => { await cancelCampaign(campaignId); await loadScheduled(); }}
          navigate={navigate}
          editingDateCampaignId={editingDateCampaignId}
          editDate={editDate}
          editTime={editTime}
          editTimezone={editTimezone}
          editDateSaving={editDateSaving}
          editDateError={editDateError}
          onStartEditDate={handleStartEditDate}
          onCancelEditDate={handleCancelEditDate}
          onSaveEditDate={handleSaveEditDate}
          onEditDateChange={setEditDate}
          onEditTimeChange={setEditTime}
          onEditTimezoneChange={setEditTimezone}
        />
      )}

      {activeTab === 'sent' && (
        <LegacyTable
          campaigns={campaigns}
          total={legacyTotal}
          loading={legacyLoading}
          error={legacyError}
          page={page}
          totalPages={Math.ceil(legacyTotal / PAGE_SIZE)}
          onPageChange={setPage}
          emptyMessage="No sent campaigns yet."
          dateHeaderLabel="Date Sent"
          navigate={navigate}
        />
      )}

      {activeTab === 'all' && (
        <AllCampaignsTab
          campaigns={campaigns}
          total={legacyTotal}
          loading={legacyLoading}
          error={legacyError}
          page={page}
          totalPages={totalPages}
          statusFilter={statusFilter}
          onStatusFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
          onPageChange={setPage}
          navigate={navigate}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={(v) => { setDateFrom(v); setPage(1); }}
          onDateToChange={(v) => { setDateTo(v); setPage(1); }}
        />
      )}

      {activeTab === 'templates' && <TemplatesTab />}

      {/* Preview modal */}
      {previewCampaign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPreviewCampaign(null)}>
          <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex justify-end">
              <button
                onClick={() => setPreviewCampaign(null)}
                className="p-1.5 bg-white rounded-full shadow text-warm-500 hover:text-warm-700"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <CampaignEmailPreview
              campaignId={previewCampaign.id}
              initialSubject={previewCampaign.subject}
            />
          </div>
        </div>
      )}

      {/* Recipients modal */}
      {recipientsModal && (
        <RecipientsModal
          campaignId={recipientsModal.campaignId}
          campaignName={recipientsModal.campaignName}
          onClose={() => setRecipientsModal(null)}
        />
      )}

      {/* Confirm modal */}
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          confirmVariant={confirmAction.variant}
          onConfirm={async () => {
            await confirmAction.action();
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Queue Tab
// ===========================================================================

interface QueueTabProps {
  data: CampaignQueueResponse | null;
  loading: boolean;
  error: string | null;
  // SDA-187: dismissible action-error shown ABOVE the list (doesn't replace it).
  actionError: string | null;
  onDismissActionError: () => void;
  // SDA-222: dismissible success banner (e.g. after an inline re-date).
  actionSuccess: string | null;
  onDismissActionSuccess: () => void;
  actionLoading: string | null;
  addReminderForChain: string | null;
  setAddReminderForChain: (id: string | null) => void;
  reminderDate: string;
  setReminderDate: (v: string) => void;
  reminderTime: string;
  setReminderTime: (v: string) => void;
  reminderTimezone: string;
  setReminderTimezone: (v: string) => void;
  reminderError: string | null;
  setReminderError: (v: string | null) => void;
  onApproveChain: (tourStopId: string) => void;
  onDismissChain: (tourStopId: string) => void;
  onAddReminder: (tourStopId: string) => void;
  onPreview: (campaign: QueuedCampaign) => void;
  onDeleteCampaign: (campaignId: string) => void;
  onViewRecipients: (campaignId: string, campaignName: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  // Inline date editing
  editingDateCampaignId: string | null;
  editDate: string;
  editTime: string;
  editTimezone: string;
  editDateSaving: boolean;
  editDateError: string | null;
  onStartEditDate: (campaign: QueuedCampaign) => void;
  onCancelEditDate: () => void;
  onSaveEditDate: (campaignId: string) => void;
  onEditDateChange: (v: string) => void;
  onEditTimeChange: (v: string) => void;
  onEditTimezoneChange: (v: string) => void;
}

function QueueTab({
  data,
  loading,
  error,
  actionError,
  onDismissActionError,
  actionSuccess,
  onDismissActionSuccess,
  actionLoading,
  addReminderForChain,
  setAddReminderForChain,
  reminderDate,
  setReminderDate,
  reminderTime,
  setReminderTime,
  reminderTimezone,
  setReminderTimezone,
  reminderError,
  setReminderError,
  onApproveChain,
  onDismissChain,
  onAddReminder,
  onPreview,
  onDeleteCampaign,
  onViewRecipients,
  navigate,
  editingDateCampaignId,
  editDate,
  editTime,
  editTimezone,
  editDateSaving,
  editDateError,
  onStartEditDate,
  onCancelEditDate,
  onSaveEditDate,
  onEditDateChange,
  onEditTimeChange,
  onEditTimezoneChange,
}: QueueTabProps) {
  // SDA-193: list-level search + sort + type filter over the FULL queue.
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<QueueSort>('soonest');
  const [filterType, setFilterType] = useState<QueueTypeFilter>('all');
  // SDA-222: narrow to only chains blocked from approval by a past-dated campaign.
  const [overdueOnly, setOverdueOnly] = useState(false);

  // How many chains are currently blocked (drives the toggle count + auto-clear).
  const overdueCount = useMemo(
    () => (data?.chains ?? []).filter(chainHasOverdue).length,
    [data],
  );

  // Apply free-text search, then type filter, then the overdue filter, then sort.
  // "soonest" (default) answers "when are these due" by ordering on each chain's
  // earliest scheduled_for (nulls sort last).
  const visibleChains = useMemo(() => {
    const all = data?.chains ?? [];
    const query = search.trim().toLowerCase();
    const searched = query === ''
      ? all
      : all.filter((chain) =>
          [chain.show_name, chain.city, chain.state, chain.theater]
            .some((field) => field?.toLowerCase().includes(query))
        );
    const filtered = filterType === 'all'
      ? searched
      : searched.filter((chain) => getChainType(chain) === filterType);
    const filtered2 = overdueOnly ? filtered.filter(chainHasOverdue) : filtered;

    const sorted = [...filtered2];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'soonest': {
          const aNext = getNextSendAt(a);
          const bNext = getNextSendAt(b);
          if (aNext === null && bNext === null) return 0;
          if (aNext === null) return 1; // nulls last
          if (bNext === null) return -1;
          return aNext < bNext ? -1 : aNext > bNext ? 1 : 0;
        }
        case 'tour': {
          // Nulls last, consistent with the 'soonest' case.
          const aDate = a.start_date;
          const bDate = b.start_date;
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
        }
        case 'show': {
          const aName = a.show_name;
          const bName = b.show_name;
          if (!aName && !bName) return 0;
          if (!aName) return 1;
          if (!bName) return -1;
          return aName.localeCompare(bName);
        }
        case 'type':
          return getChainType(a).localeCompare(getChainType(b));
        default:
          return 0;
      }
    });
    return sorted;
  }, [data, search, sortBy, filterType, overdueOnly]);

  // SDA-187: dismissible action error, shown ABOVE every state (incl. a load
  // error) so a failed action is never hidden, and never replaces the list.
  const actionErrorBanner = actionError ? (
    <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 flex items-start justify-between gap-4">
      <span>{actionError}</span>
      <button type="button" onClick={onDismissActionError} className="shrink-0 underline font-medium">
        Dismiss
      </button>
    </div>
  ) : null;

  // SDA-222: success banner mirrors the error banner (green variant), shown ABOVE
  // every state so a successful re-date is explicitly confirmed.
  const actionSuccessBanner = actionSuccess ? (
    <div className="mb-4 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700 flex items-start justify-between gap-4">
      <span>{actionSuccess}</span>
      <button type="button" onClick={onDismissActionSuccess} className="shrink-0 underline font-medium">
        Dismiss
      </button>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-warm-200 p-6 animate-pulse">
            <div className="h-6 bg-warm-100 rounded w-2/3 mb-3" />
            <div className="h-4 bg-warm-100 rounded w-1/2 mb-4" />
            <div className="h-24 bg-warm-50 rounded mb-4" />
            <div className="flex justify-between">
              <div className="h-10 bg-warm-100 rounded w-32" />
              <div className="h-10 bg-warm-100 rounded w-40" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {actionErrorBanner}
        {actionSuccessBanner}
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      </div>
    );
  }

  if (!data || data.chains.length === 0) {
    return (
      <div>
        {actionErrorBanner}
        {actionSuccessBanner}
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">Queue is empty</h3>
          <p className="text-warm-600 mb-4">
            No campaigns are waiting for approval. New campaigns will appear here when tour stops are auto-queued.
          </p>
        </div>
      </div>
    );
  }

  // SDA-193: search + sort + type-filter toolbar, styled like the All Campaigns filter row.
  const toolbar = (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-start sm:items-end">
        <div className="w-full sm:min-w-[220px] sm:w-auto">
          <label className="block text-sm font-medium text-warm-700 mb-1">Search</label>
          <input
            type="text"
            aria-label="Search queue"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search show, city…"
            className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="w-full sm:min-w-[170px] sm:w-auto">
          <label className="block text-sm font-medium text-warm-700 mb-1">Sort by</label>
          <select
            aria-label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as QueueSort)}
            className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="soonest">Soonest send</option>
            <option value="tour">Tour date</option>
            <option value="show">Show A–Z</option>
            <option value="type">Type</option>
          </select>
        </div>
        <div className="w-full sm:min-w-[170px] sm:w-auto">
          <label className="block text-sm font-medium text-warm-700 mb-1">Filter type</label>
          <select
            aria-label="Filter type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as QueueTypeFilter)}
            className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All</option>
            <option value="tour_announcement">Tour Announcements</option>
            <option value="reminder">Reminders</option>
          </select>
        </div>
        {/* SDA-222: narrow to chains blocked from approval by a past-dated campaign. */}
        <label className="flex items-center gap-2 sm:self-end sm:min-h-[44px] text-sm font-medium text-warm-700 cursor-pointer select-none">
          <input
            type="checkbox"
            aria-label="Overdue only"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
            className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
          />
          Overdue only
          {overdueCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-danger-100 text-danger-700">
              {overdueCount}
            </span>
          )}
        </label>
        <div className="flex items-center sm:self-end sm:min-h-[44px] text-sm text-warm-500">
          Showing {visibleChains.length} of {data.chains.length}
        </div>
      </div>
    </div>
  );

  // Filter produced no chains (queue itself is non-empty — distinct message).
  if (visibleChains.length === 0) {
    return (
      <div className="space-y-6">
        {actionErrorBanner}
        {actionSuccessBanner}
        {toolbar}
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No chains match this filter</h3>
          <p className="text-warm-600">
            {overdueOnly
              ? 'No chains are overdue. Clear the “Overdue only” filter, or adjust your search or type filter, to see queued chains.'
              : 'Try a different search or type filter to see queued chains.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {actionErrorBanner}
      {actionSuccessBanner}
      {toolbar}
      {visibleChains.map((chain) => (
        <ChainCard
          key={chain.tour_stop_id}
          chain={chain}
          actionLoading={actionLoading}
          addingReminder={addReminderForChain === chain.tour_stop_id}
          onToggleAddReminder={() =>
            setAddReminderForChain(addReminderForChain === chain.tour_stop_id ? null : chain.tour_stop_id)
          }
          reminderDate={reminderDate}
          setReminderDate={(v) => { setReminderDate(v); setReminderError(null); }}
          reminderTime={reminderTime}
          setReminderTime={setReminderTime}
          reminderTimezone={reminderTimezone}
          setReminderTimezone={setReminderTimezone}
          reminderError={reminderError}
          onApprove={() => onApproveChain(chain.tour_stop_id)}
          onDismiss={() => onDismissChain(chain.tour_stop_id)}
          onAddReminder={() => onAddReminder(chain.tour_stop_id)}
          onPreview={onPreview}
          onDeleteCampaign={onDeleteCampaign}
          onViewRecipients={onViewRecipients}
          navigate={navigate}
          editingDateCampaignId={editingDateCampaignId}
          editDate={editDate}
          editTime={editTime}
          editTimezone={editTimezone}
          editDateSaving={editDateSaving}
          editDateError={editDateError}
          onStartEditDate={onStartEditDate}
          onCancelEditDate={onCancelEditDate}
          onSaveEditDate={onSaveEditDate}
          onEditDateChange={onEditDateChange}
          onEditTimeChange={onEditTimeChange}
          onEditTimezoneChange={onEditTimezoneChange}
        />
      ))}
    </div>
  );
}

// ===========================================================================
// Chain Card
// ===========================================================================

interface ChainCardProps {
  chain: CampaignChainTourStop;
  actionLoading: string | null;
  addingReminder: boolean;
  onToggleAddReminder: () => void;
  reminderDate: string;
  setReminderDate: (v: string) => void;
  reminderTime: string;
  setReminderTime: (v: string) => void;
  reminderTimezone: string;
  setReminderTimezone: (v: string) => void;
  reminderError: string | null;
  onApprove: () => void;
  onDismiss: () => void;
  onAddReminder: () => void;
  onPreview: (campaign: QueuedCampaign) => void;
  onDeleteCampaign: (campaignId: string) => void;
  onViewRecipients: (campaignId: string, campaignName: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  // Inline date editing
  editingDateCampaignId: string | null;
  editDate: string;
  editTime: string;
  editTimezone: string;
  editDateSaving: boolean;
  editDateError: string | null;
  onStartEditDate: (campaign: QueuedCampaign) => void;
  onCancelEditDate: () => void;
  onSaveEditDate: (campaignId: string) => void;
  onEditDateChange: (v: string) => void;
  onEditTimeChange: (v: string) => void;
  onEditTimezoneChange: (v: string) => void;
}

function ChainCard({
  chain,
  actionLoading,
  addingReminder,
  onToggleAddReminder,
  reminderDate,
  setReminderDate,
  reminderTime,
  setReminderTime,
  reminderTimezone,
  setReminderTimezone,
  reminderError,
  onApprove,
  onDismiss,
  onAddReminder,
  onPreview,
  onDeleteCampaign,
  onViewRecipients,
  navigate,
  editingDateCampaignId,
  editDate,
  editTime,
  editTimezone,
  editDateSaving,
  editDateError,
  onStartEditDate,
  onCancelEditDate,
  onSaveEditDate,
  onEditDateChange,
  onEditTimeChange,
  onEditTimezoneChange,
}: ChainCardProps) {
  const isLoading = actionLoading === chain.tour_stop_id || actionLoading === `add-reminder-${chain.tour_stop_id}`;
  const firstCampaign = chain.campaigns[0];
  const audienceCount = firstCampaign?.estimated_audience;
  const cadenceWarning = chain.campaigns.find((c) => c.cadence_warning)?.cadence_warning;
  const targetCities = firstCampaign?.target_cities ?? [];
  // SDA-193: earliest scheduled send across the chain — the "next due" date.
  const nextSendAt = getNextSendAt(chain);
  const nextSendTz = nextSendAt
    ? chain.campaigns.find((c) => c.scheduled_for === nextSendAt)?.scheduled_timezone ?? null
    : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-warm-200 p-6">
      {/* Tour stop header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <h3 className="text-lg font-semibold text-warm-900">
            <Link to={`/admin/shows/${chain.show_id}/edit`} className="hover:underline">
              {chain.show_name}
            </Link> &mdash; {chain.city}, {chain.state}
          </h3>
          <Link
            to={`/admin/tour-stops/${chain.tour_stop_id}/edit`}
            className="text-xs text-primary-600 hover:underline"
          >
            View Tour Stop
          </Link>
        </div>
        <p className="text-sm text-warm-500 mt-0.5">
          {chain.theater} &middot; {formatShortDate(chain.start_date)} &ndash; {formatShortDate(chain.end_date)}
        </p>
        {nextSendAt && (
          <p className="text-sm font-medium text-warm-700 mt-0.5">
            Next send: {formatDateTime(nextSendAt, nextSendTz)}
          </p>
        )}

        <p className="text-sm text-warm-600 mt-2">
          {audienceCount == null ? (
            <span className="inline-flex items-center gap-1.5 text-warm-500">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-warm-400 animate-pulse" aria-hidden />
              <span>Calculating audience…</span>
            </span>
          ) : (
            <>
              {audienceCount} studios would receive{' '}
              <Tooltip text={`Studios in ${chain.city} that accept marketing emails and haven't booked this show yet`} />
            </>
          )}
        </p>

        {firstCampaign && (
          <button
            type="button"
            onClick={() => onViewRecipients(firstCampaign.id, firstCampaign.name)}
            className="mt-1 text-sm text-primary-600 hover:text-primary-700 hover:underline font-medium min-h-[44px] sm:min-h-0"
          >
            View Recipients
          </button>
        )}

        {/* Target areas */}
        {targetCities.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="text-xs text-warm-500 mr-1">Target Areas:</span>
            {targetCities.map((city) => (
              <span key={city.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-warm-100 text-warm-700">
                {city.name}, {city.state}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Cadence warning */}
      {cadenceWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Cadence Alerts</span>
            <Tooltip text="These warnings appear when other campaigns target the same studios. Review to avoid sending too many emails close together." />
          </div>
          <ul className="space-y-1 ml-6">
            {cadenceWarning.split('\n').map((line, i) => (
              <li key={i} className="text-sm text-amber-800 list-disc">{line}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Email sequence */}
      <div className="border border-warm-200 rounded-lg mb-4">
        <div className="px-4 py-2.5 bg-warm-50 border-b border-warm-200 rounded-t-lg">
          <h4 className="text-sm font-semibold text-warm-700">Email Sequence</h4>
        </div>
        <div className="divide-y divide-warm-100">
          {chain.campaigns.map((campaign, idx) => {
            // SDA-222: the exact campaign that blocks Approve All & Schedule —
            // tint it so the overdue one is unmissable (the approve funnel also
            // scrolls to this row via its id).
            const blocking = isBlockingOverdue(campaign.scheduled_for) && campaign.status === 'pending_approval';
            return (
            <div
              key={campaign.id}
              id={`campaign-row-${campaign.id}`}
              className={`px-4 py-3 ${blocking ? 'bg-danger-50 border-l-4 border-danger-400' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-warm-900">
                    {idx + 1}. {getTypeBadge(campaign.campaign_type)}{' '}
                    {editingDateCampaignId !== campaign.id && campaign.scheduled_for && (
                      <button
                        type="button"
                        onClick={() => onStartEditDate(campaign)}
                        className="text-warm-500 font-normal ml-1 hover:text-primary-600 hover:underline cursor-pointer transition-colors"
                        title="Click to edit date"
                      >
                        {formatDateTime(campaign.scheduled_for, campaign.scheduled_timezone)}
                        {campaign.scheduled_timezone && TZ_ABBR[campaign.scheduled_timezone] && (
                          <span className="ml-1 text-warm-400">{TZ_ABBR[campaign.scheduled_timezone]}</span>
                        )}
                        {isBlockingOverdue(campaign.scheduled_for) && campaign.status === 'pending_approval' && (
                          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger-100 text-danger-700 uppercase">Overdue</span>
                        )}
                        <svg className="w-3 h-3 inline-block ml-1 text-warm-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </p>

                  {/* Inline date editor */}
                  {editingDateCampaignId === campaign.id && (
                    <InlineDateEditor
                      editDate={editDate}
                      editTime={editTime}
                      editTimezone={editTimezone}
                      editDateSaving={editDateSaving}
                      editDateError={editDateError}
                      onDateChange={onEditDateChange}
                      onTimeChange={onEditTimeChange}
                      onTimezoneChange={onEditTimezoneChange}
                      onSave={() => onSaveEditDate(campaign.id)}
                      onCancel={onCancelEditDate}
                    />
                  )}

                  <p className="text-sm text-warm-500 truncate mt-0.5">
                    &ldquo;{campaign.subject}&rdquo;
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit`)}
                    className="px-3 py-1.5 text-xs font-medium text-warm-600 border border-warm-300 rounded-lg hover:bg-warm-50 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onPreview(campaign)}
                    className="px-3 py-1.5 text-xs font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    Preview
                  </button>
                  {campaign.sequence_number > 1 && (
                    <button
                      onClick={() => onDeleteCampaign(campaign.id)}
                      className="text-warm-400 hover:text-danger-500 transition-colors p-1"
                      title="Remove reminder"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {/* Add reminder */}
        <div className="px-4 py-3 border-t border-warm-100">
          {addingReminder ? (
            <div className="space-y-3">
              {/* Preset buttons */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-warm-500 self-center mr-1">Quick presets:</span>
                {(() => {
                  const tourDate = new Date(chain.start_date);
                  const twoWeeks = new Date(tourDate);
                  twoWeeks.setDate(twoWeeks.getDate() - 14);
                  const oneWeek = new Date(tourDate);
                  oneWeek.setDate(oneWeek.getDate() - 7);
                  const fmt = (d: Date) => d.toISOString().slice(0, 10);
                  const today = fmt(new Date());
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => setReminderDate(fmt(twoWeeks))}
                        disabled={fmt(twoWeeks) < today}
                        className="px-2.5 py-1 text-xs border border-warm-300 rounded-lg hover:bg-warm-50 text-warm-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        2 weeks before tour ({formatShortDate(fmt(twoWeeks))})
                      </button>
                      <button
                        type="button"
                        onClick={() => setReminderDate(fmt(oneWeek))}
                        disabled={fmt(oneWeek) < today}
                        className="px-2.5 py-1 text-xs border border-warm-300 rounded-lg hover:bg-warm-50 text-warm-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        1 week before tour ({formatShortDate(fmt(oneWeek))})
                      </button>
                    </>
                  );
                })()}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-warm-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={reminderDate}
                    min={todayInTimezone()}
                    onChange={(e) => setReminderDate(e.target.value)}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {reminderError && <p className="text-danger-600 text-xs mt-1">{reminderError}</p>}
                </div>
                <div className="w-full sm:w-28">
                  <label className="block text-xs font-medium text-warm-600 mb-1">Time</label>
                  <input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="w-full sm:w-40">
                  <label className="block text-xs font-medium text-warm-600 mb-1">Timezone</label>
                  <select
                    value={reminderTimezone}
                    onChange={(e) => setReminderTimezone(e.target.value)}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="America/New_York">Eastern</option>
                    <option value="America/Chicago">Central</option>
                    <option value="America/Denver">Mountain</option>
                    <option value="America/Los_Angeles">Pacific</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onAddReminder}
                  disabled={!reminderDate || actionLoading === `add-reminder-${chain.tour_stop_id}`}
                  className="px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {actionLoading === `add-reminder-${chain.tour_stop_id}` ? 'Adding...' : 'Add Reminder'}
                </button>
                <button
                  type="button"
                  onClick={onToggleAddReminder}
                  className="px-4 py-2 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onToggleAddReminder}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium min-h-[44px] flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Another Reminder
            </button>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-warm-100">
        <button
          type="button"
          onClick={onDismiss}
          disabled={isLoading}
          className="px-4 py-2.5 min-h-[44px] text-sm font-medium text-danger-600 border border-danger-300 rounded-lg hover:bg-danger-50 disabled:opacity-50 transition-colors"
        >
          Dismiss Chain
        </button>
        <button
          type="button"
          onClick={onApprove}
          disabled={isLoading}
          className="px-5 py-2.5 min-h-[44px] text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {isLoading ? 'Processing...' : 'Approve All & Schedule'}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Scheduled Sends Tab
// ===========================================================================

interface ScheduledTabProps {
  data: ScheduledSendsResponse | null;
  loading: boolean;
  error: string | null;
  onViewRecipients: (campaignId: string, campaignName: string) => void;
  onCancel: (campaignId: string) => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
  // Inline reschedule (shared with the Queue tab)
  editingDateCampaignId: string | null;
  editDate: string;
  editTime: string;
  editTimezone: string;
  editDateSaving: boolean;
  editDateError: string | null;
  onStartEditDate: (c: { id: string; scheduled_for: string | null; scheduled_timezone: string | null }) => void;
  onCancelEditDate: () => void;
  onSaveEditDate: (id: string) => void;
  onEditDateChange: (v: string) => void;
  onEditTimeChange: (v: string) => void;
  onEditTimezoneChange: (v: string) => void;
}

function ScheduledTab({
  data,
  loading,
  error,
  onViewRecipients,
  onCancel,
  navigate,
  editingDateCampaignId,
  editDate,
  editTime,
  editTimezone,
  editDateSaving,
  editDateError,
  onStartEditDate,
  onCancelEditDate,
  onSaveEditDate,
  onEditDateChange,
  onEditTimeChange,
  onEditTimezoneChange,
}: ScheduledTabProps) {
  const [cancelTarget, setCancelTarget] = useState<{ id: string; name: string } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // SDA-245: narrow the tab to one status bucket
  const [statusFilter, setStatusFilter] = useState<'all' | 'sending' | 'scheduled' | 'pending_approval'>('all');

  if (loading) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3 text-sm text-warm-600">
          <div className="w-4 h-4 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          <span>Loading scheduled sends — counting recipients, this can take a moment…</span>
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-5 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
        {error}
      </div>
    );
  }

  if (!data || data.scheduled.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-warm-900 mb-2">No scheduled sends</h3>
        <p className="text-warm-600">
          Campaigns will appear here once they are approved and scheduled.
        </p>
      </div>
    );
  }

  // Quota-parked campaigns (cron flipped them to 'sending', then parked them
  // when the daily send limit ran out) render in a pinned section on top,
  // sorted by estimated completion (i.e. drain order — rows without an
  // estimate sink to the bottom); everything else keeps the month grouping.
  const waitingAll = data.scheduled
    .filter((i) => i.status === 'sending')
    .sort((a, b) => (a.est_complete_at ?? '9999-99-99').localeCompare(b.est_complete_at ?? '9999-99-99'));
  const upcomingAll = data.scheduled.filter((i) => i.status !== 'sending');
  const scheduledCount = upcomingAll.filter((i) => i.status === 'scheduled').length;
  const pendingCount = upcomingAll.filter((i) => i.status === 'pending_approval').length;

  // Apply the status filter: the waiting group shows for 'all'/'sending',
  // the month groups show the (possibly narrowed) upcoming statuses.
  const waitingItems = statusFilter === 'all' || statusFilter === 'sending' ? waitingAll : [];
  const upcomingItems =
    statusFilter === 'all'
      ? upcomingAll
      : statusFilter === 'sending'
        ? []
        : upcomingAll.filter((i) => i.status === statusFilter);

  // Group by month
  const grouped: { label: string; items: ScheduledSend[] }[] = [];
  let currentMonth = '';

  for (const item of upcomingItems) {
    const month = formatMonthYear(item.scheduled_for);
    if (month !== currentMonth) {
      currentMonth = month;
      grouped.push({ label: month, items: [] });
    }
    grouped[grouped.length - 1]!.items.push(item);
  }

  // Estimate subtext for a waiting row: when the backend simulated a drain
  // window (first send day → last send day, weekends excluded, recomputed
  // from the current daily limit on every load), spell it out; otherwise
  // fall back to the generic line.
  const waitingEta = (item: ScheduledSend) => (
    item.est_start_at && item.est_complete_at ? (
      <p className="text-xs text-warm-500">
        {item.est_start_at === item.est_complete_at
          ? `Est. to send ~${formatEstDate(item.est_start_at)}`
          : `Est. to start ~${formatEstDate(item.est_start_at)}, finish ~${formatEstDate(item.est_complete_at)}`}
      </p>
    ) : (
      <p className="text-xs text-warm-400">Sends automatically as quota frees up</p>
    )
  );

  // Waiting rows differ from upcoming rows in three ways: the status badge, a
  // date subtext, and View-only actions (the backend rejects Edit date and
  // Cancel for any status other than 'scheduled').
  const renderGroup = (label: string, items: ScheduledSend[], waiting: boolean) => (
    <div key={label}>
      <h3 className="text-sm font-semibold text-warm-500 uppercase tracking-wider mb-3">
        {label}
      </h3>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-warm-50 border-b border-warm-100">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Date</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Campaign</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Type</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Recipients</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Status</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-warm-100">
            {items.map((item) => (
              <Fragment key={item.id}>
              <tr className="hover:bg-warm-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <p className="text-warm-900 font-medium">
                      {formatDateTime(item.scheduled_for, item.scheduled_timezone)}
                    </p>
                    {item.scheduled_timezone && TZ_ABBR[item.scheduled_timezone] && (
                      <span className="text-warm-400 text-xs">{TZ_ABBR[item.scheduled_timezone]}</span>
                    )}
                    {waiting && waitingEta(item)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-warm-900">{item.name}</p>
                    {item.show_name && (
                      <p className="text-sm text-warm-500">
                        {item.show_name}
                        {item.city ? ` - ${item.city}` : ''}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">{getTypeBadge(item.campaign_type)}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => onViewRecipients(item.id, item.name)}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    {item.estimated_audience ?? 0}
                  </button>
                </td>
                <td className="px-6 py-4">
                  {waiting ? (
                    <div>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700">
                        Waiting on Send Limit
                      </span>
                      {item.sent_count > 0 && (
                        <p className="text-xs text-warm-500 mt-1">{item.sent_count} sent so far</p>
                      )}
                    </div>
                  ) : (
                    getStatusBadge(item.status)
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {!waiting && (
                      <button
                        onClick={() => onStartEditDate(item)}
                        className="px-3 py-1.5 text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
                      >
                        Edit date
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/admin/campaigns/${item.id}`)}
                      className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      View
                    </button>
                    {!waiting && (
                      <button
                        onClick={() => setCancelTarget({ id: item.id, name: item.name })}
                        className="px-3 py-1.5 text-sm text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {!waiting && editingDateCampaignId === item.id && (
                <tr className="bg-warm-50">
                  <td colSpan={6} className="px-6 pb-4">
                    <InlineDateEditor
                      editDate={editDate}
                      editTime={editTime}
                      editTimezone={editTimezone}
                      editDateSaving={editDateSaving}
                      editDateError={editDateError}
                      onDateChange={onEditDateChange}
                      onTimeChange={onEditTimeChange}
                      onTimezoneChange={onEditTimezoneChange}
                      onSave={() => onSaveEditDate(item.id)}
                      onCancel={onCancelEditDate}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {items.map((item) => (
          <div
            key={item.id}
            className="block w-full text-left bg-white rounded-xl border border-warm-200 p-4 hover:border-warm-300"
          >
            <div className="flex items-center gap-2 mb-1">
              {getTypeBadge(item.campaign_type)}
              {waiting ? (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700">
                  Waiting on Send Limit
                </span>
              ) : (
                getStatusBadge(item.status)
              )}
            </div>
            {waiting && item.sent_count > 0 && (
              <p className="text-xs text-warm-500 mb-1">{item.sent_count} sent so far</p>
            )}
            <p className="font-medium text-warm-900 mb-0.5">{item.name}</p>
            {item.show_name && (
              <p className="text-sm text-warm-500 mb-1">{item.show_name}{item.city ? ` - ${item.city}` : ''}</p>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-warm-600">
                {formatDateTime(item.scheduled_for, item.scheduled_timezone)}
                {item.scheduled_timezone && TZ_ABBR[item.scheduled_timezone] && (
                  <span className="ml-1 text-warm-400">{TZ_ABBR[item.scheduled_timezone]}</span>
                )}
              </span>
              <button
                onClick={() => onViewRecipients(item.id, item.name)}
                className="text-primary-600 hover:underline font-medium min-h-[44px] flex items-center"
              >
                {item.estimated_audience ?? 0} recipients
              </button>
            </div>
            {waiting && <div className="mt-1">{waitingEta(item)}</div>}
            <div className="mt-2 flex items-center gap-4">
              {!waiting && (
                <button
                  onClick={() => onStartEditDate(item)}
                  className="text-sm text-warm-600 hover:underline font-medium min-h-[44px] flex items-center"
                >
                  Edit date
                </button>
              )}
              <button
                onClick={() => navigate(`/admin/campaigns/${item.id}`)}
                className="text-sm text-primary-600 hover:underline font-medium min-h-[44px] flex items-center"
              >
                View
              </button>
              {!waiting && (
                <button
                  onClick={() => setCancelTarget({ id: item.id, name: item.name })}
                  className="text-sm text-danger-600 hover:underline font-medium min-h-[44px] flex items-center"
                >
                  Cancel
                </button>
              )}
            </div>
            {!waiting && editingDateCampaignId === item.id && (
              <InlineDateEditor
                editDate={editDate}
                editTime={editTime}
                editTimezone={editTimezone}
                editDateSaving={editDateSaving}
                editDateError={editDateError}
                onDateChange={onEditDateChange}
                onTimeChange={onEditTimeChange}
                onTimezoneChange={onEditTimezoneChange}
                onSave={() => onSaveEditDate(item.id)}
                onCancel={onCancelEditDate}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* SDA-245: status filter (same control style as the Queue tab) */}
      <div className="w-full sm:min-w-[240px] sm:w-fit">
        <label className="block text-sm font-medium text-warm-700 mb-1">Filter status</label>
        <select
          aria-label="Filter status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">All ({data.scheduled.length})</option>
          <option value="sending">Waiting on Send Limit ({waitingAll.length})</option>
          <option value="scheduled">Scheduled ({scheduledCount})</option>
          <option value="pending_approval">Pending Approval ({pendingCount})</option>
        </select>
      </div>

      {waitingItems.length > 0 &&
        renderGroup(`Waiting on Daily Send Limit (${waitingItems.length})`, waitingItems, true)}
      {grouped.map((group) => renderGroup(group.label, group.items, false))}
      {waitingItems.length === 0 && grouped.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-warm-600">
          No campaigns match this filter.
        </div>
      )}

      {cancelTarget && (
        <ConfirmModal
          title="Cancel scheduled campaign?"
          message={
            (cancelError ? `${cancelError}\n\n` : '') +
            `"${cancelTarget.name}" will not be sent. This can't be undone, but you can re-create or re-schedule it later.`
          }
          confirmLabel={cancelling ? 'Cancelling…' : 'Cancel send'}
          confirmVariant="danger"
          onConfirm={async () => {
            if (cancelling) return;
            setCancelling(true);
            setCancelError(null);
            try {
              await onCancel(cancelTarget.id);
              setCancelTarget(null);
            } catch (err) {
              setCancelError(err instanceof Error ? err.message : 'Failed to cancel campaign');
            } finally {
              setCancelling(false);
            }
          }}
          onCancel={() => { setCancelTarget(null); setCancelError(null); }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// All Campaigns Tab (grouped/flat toggle)
// ===========================================================================

/** Extract city info from campaign name, e.g. "Hamilton - Chicago, IL (Announcement)" → "Chicago, IL" */
function extractCityFromName(name: string): string | null {
  const match = name.match(/-\s*(.+?)\s*\(/);
  return match?.[1]?.trim() ?? null;
}

/** Get the recipient count from a campaign */
function getRecipientCount(c: LegacyCampaign): number {
  if (c.sent_count > 0) return c.sent_count;
  if (c.email_sends && c.email_sends.length > 0) return c.email_sends[0]!.count;
  return 0;
}

/** Build a summary line like "6 recipients  |  Chicago, IL" */
function CampaignSummaryLine({ campaign }: { campaign: LegacyCampaign }) {
  const count = getRecipientCount(campaign);
  const city = extractCityFromName(campaign.name);
  const parts: string[] = [];
  if (count > 0) parts.push(`${count} recipient${count !== 1 ? 's' : ''}`);
  if (city) parts.push(city);
  if (parts.length === 0) return null;
  return <p className="text-sm text-warm-500 mt-0.5">{parts.join('  |  ')}</p>;
}

interface AllCampaignsTabProps {
  campaigns: LegacyCampaign[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  statusFilter: CampaignStatus;
  onStatusFilterChange: (v: CampaignStatus) => void;
  onPageChange: (p: number) => void;
  navigate: ReturnType<typeof useNavigate>;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
}

function AllCampaignsTab({
  campaigns,
  total,
  loading,
  error,
  page,
  totalPages,
  statusFilter,
  onStatusFilterChange,
  onPageChange,
  navigate,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}: AllCampaignsTabProps) {
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
  const [expandedChains, setExpandedChains] = useState<Set<string>>(new Set());
  const [hidePendingApproval, setHidePendingApproval] = useState(false);

  const displayCampaigns = hidePendingApproval
    ? campaigns.filter(c => c.status !== 'pending_approval')
    : campaigns;

  const toggleChain = (key: string) => {
    setExpandedChains((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Group campaigns by tour_stop_id for grouped view
  const groupedCampaigns = (() => {
    if (viewMode !== 'grouped') return null;
    const groups: { key: string; announcement: LegacyCampaign; reminders: LegacyCampaign[] }[] = [];
    const standalone: LegacyCampaign[] = [];
    const chainMap = new Map<string, { announcement: LegacyCampaign | null; reminders: LegacyCampaign[] }>();

    for (const c of displayCampaigns) {
      if (!c.tour_stop_id) {
        standalone.push(c);
        continue;
      }
      if (!chainMap.has(c.tour_stop_id)) {
        chainMap.set(c.tour_stop_id, { announcement: null, reminders: [] });
      }
      const group = chainMap.get(c.tour_stop_id)!;
      if (c.sequence_number === 1) {
        group.announcement = c;
      } else {
        group.reminders.push(c);
      }
    }

    for (const [key, group] of chainMap) {
      if (group.announcement) {
        groups.push({ key, announcement: group.announcement, reminders: group.reminders });
      } else {
        // No announcement found, treat reminders as standalone
        standalone.push(...group.reminders);
      }
    }

    return { groups, standalone };
  })();

  return (
    <>
      {/* Status filter + view toggle */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-start sm:items-end">
            <div className="w-full sm:min-w-[150px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value as CampaignStatus)}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="scheduled">Scheduled</option>
                <option value="sending">Sending</option>
                <option value="sent">Sent</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-warm-700 cursor-pointer self-end min-h-[44px]">
              <input
                type="checkbox"
                checked={hidePendingApproval}
                onChange={(e) => setHidePendingApproval(e.target.checked)}
                className="w-4 h-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="whitespace-nowrap">Hide Pending Approval</span>
            </label>
            <div className="w-full sm:min-w-[150px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="w-full sm:min-w-[150px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => onDateToChange(e.target.value)}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            {(statusFilter !== 'all' || dateFrom || dateTo || hidePendingApproval) && (
              <div className="flex items-end">
                <button
                  onClick={() => { onStatusFilterChange('all'); onDateFromChange(''); onDateToChange(''); setHidePendingApproval(false); }}
                  className="px-4 py-2.5 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-1 bg-warm-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => setViewMode('grouped')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'grouped' ? 'bg-white shadow-sm text-warm-900' : 'text-warm-500'
              }`}
            >
              Grouped
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'flat' ? 'bg-white shadow-sm text-warm-900' : 'text-warm-500'
              }`}
            >
              Flat
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'flat' ? (
        <LegacyTable
          campaigns={displayCampaigns}
          total={total}
          loading={loading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          emptyMessage={
            statusFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Create your first email campaign to reach studios.'
          }
          showCreateLink={statusFilter === 'all'}
          showChainBadge
          navigate={navigate}
        />
      ) : (
        <>
          {loading && displayCampaigns.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
                  <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-warm-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
              {error}
            </div>
          ) : displayCampaigns.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <h3 className="text-lg font-semibold text-warm-900 mb-2">No campaigns found</h3>
              <p className="text-warm-600">
                {statusFilter !== 'all'
                  ? 'Try adjusting your filters to see more results.'
                  : 'Create your first email campaign to reach studios.'}
              </p>
            </div>
          ) : groupedCampaigns && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-warm-100">
                {groupedCampaigns.groups.map(({ key, announcement, reminders }) => {
                  const isExpanded = expandedChains.has(key);
                  const chainSize = 1 + reminders.length;
                  return (
                    <div key={key}>
                      <div
                        className="flex items-center gap-3 px-6 py-4 hover:bg-warm-50 transition-colors cursor-pointer"
                        onClick={() => reminders.length > 0 && toggleChain(key)}
                      >
                        {reminders.length > 0 ? (
                          <svg
                            className={`w-4 h-4 text-warm-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        ) : (
                          <div className="w-4 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-warm-900 truncate">{announcement.name}</p>
                            {chainSize > 1 && (
                              <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-warm-100 text-warm-600 flex-shrink-0">
                                {chainSize} emails
                              </span>
                            )}
                            {getTypeBadge(announcement.campaign_type)}
                            {getStatusBadge(announcement.status)}
                          </div>
                          <CampaignSummaryLine campaign={announcement} />
                        </div>
                        <div className="hidden sm:flex items-center gap-4 flex-shrink-0 text-sm">
                          <span className="text-warm-500">
                            {announcement.completed_at
                              ? formatDate(announcement.completed_at)
                              : announcement.scheduled_for
                                ? formatDate(announcement.scheduled_for)
                                : formatDate(announcement.created_at)}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/campaigns/${announcement.id}`); }}
                            className="px-3 py-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            {announcement.status === 'draft' ? 'Edit' : 'View'}
                          </button>
                        </div>
                      </div>
                      {isExpanded && reminders.map((reminder) => (
                        <div
                          key={reminder.id}
                          className="flex items-center gap-3 px-6 py-3 bg-warm-50/50 border-l-2 border-warm-200 ml-6 mr-2 rounded-r"
                        >
                          <div className="w-4 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-warm-800 truncate">{reminder.name}</p>
                              {getTypeBadge(reminder.campaign_type)}
                              {getStatusBadge(reminder.status)}
                            </div>
                            <CampaignSummaryLine campaign={reminder} />
                          </div>
                          <div className="hidden sm:flex items-center gap-4 flex-shrink-0 text-sm">
                            <span className="text-warm-500">
                              {reminder.completed_at
                                ? formatDate(reminder.completed_at)
                                : reminder.scheduled_for
                                  ? formatDate(reminder.scheduled_for)
                                  : formatDate(reminder.created_at)}
                            </span>
                            <button
                              onClick={() => navigate(`/admin/campaigns/${reminder.id}`)}
                              className="px-3 py-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              {reminder.status === 'draft' ? 'Edit' : 'View'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {groupedCampaigns.standalone.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center gap-3 px-6 py-4 hover:bg-warm-50 transition-colors"
                  >
                    <div className="w-4 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-warm-900 truncate">{campaign.name}</p>
                        {getTypeBadge(campaign.campaign_type)}
                        {getStatusBadge(campaign.status)}
                      </div>
                      <CampaignSummaryLine campaign={campaign} />
                    </div>
                    <div className="hidden sm:flex items-center gap-4 flex-shrink-0 text-sm">
                      <span className="text-warm-500">
                        {campaign.completed_at
                          ? formatDate(campaign.completed_at)
                          : campaign.scheduled_for
                            ? formatDate(campaign.scheduled_for)
                            : formatDate(campaign.created_at)}
                      </span>
                      <button
                        onClick={() => navigate(`/admin/campaigns/${campaign.id}`)}
                        className="px-3 py-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        {campaign.status === 'draft' ? 'Edit' : 'View'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Pagination for grouped view */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
              <p className="text-sm text-warm-600">
                Showing {((page - 1) * PAGE_SIZE) + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} campaigns
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-warm-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ===========================================================================
// Legacy Campaign Table (Sent + All tabs)
// ===========================================================================

interface LegacyTableProps {
  campaigns: LegacyCampaign[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  emptyMessage: string;
  showCreateLink?: boolean;
  showChainBadge?: boolean;
  dateHeaderLabel?: string;
  navigate: ReturnType<typeof useNavigate>;
}

function LegacyTable({
  campaigns,
  total,
  loading,
  error,
  page,
  totalPages,
  onPageChange,
  emptyMessage,
  showCreateLink,
  showChainBadge,
  dateHeaderLabel = 'Date',
  navigate,
}: LegacyTableProps) {
  if (loading && campaigns.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
            <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
            <div className="h-4 bg-warm-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
        {error}
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-warm-900 mb-2">No campaigns found</h3>
        <p className="text-warm-600 mb-6">{emptyMessage}</p>
        {showCreateLink && (
          <Link to="/admin/campaigns/new" className="btn-primary">
            Create Campaign
          </Link>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-warm-50 border-b border-warm-100">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Campaign</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700 whitespace-nowrap">Type</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700 whitespace-nowrap">Recipients</th>
              <th className="text-left px-4 py-4 text-sm font-semibold text-warm-700 whitespace-nowrap">Opens</th>
              <th className="text-left px-4 py-4 text-sm font-semibold text-warm-700 whitespace-nowrap">Clicks</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700 whitespace-nowrap">Status</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700 whitespace-nowrap">{dateHeaderLabel}</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-warm-100">
            {campaigns.map((campaign) => (
              <tr key={campaign.id} className="hover:bg-warm-50 transition-colors">
                <td className="px-6 py-4 w-full max-w-0">
                  <div>
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-medium text-warm-900 truncate">{campaign.name}</p>
                      {showChainBadge && campaign.parent_campaign_id && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-warm-100 text-warm-500">
                          Chain
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-warm-500 truncate">{campaign.subject}</p>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{getTypeBadge(campaign.campaign_type)}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-warm-900 font-medium">
                    {campaign.sent_count || campaign.email_sends?.[0]?.count || 0}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-warm-700">{campaign.email_stats?.opened ?? '—'}</span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-warm-700">{campaign.email_stats?.clicked ?? '—'}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(campaign.status)}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm">
                    {campaign.completed_at ? (
                      <p className="text-warm-900">Sent {formatDate(campaign.completed_at)}</p>
                    ) : campaign.scheduled_for ? (
                      <div>
                        <p className="text-warm-900">Scheduled</p>
                        <p className="text-warm-500">
                          {formatDateTime(campaign.scheduled_for, campaign.scheduled_timezone)}
                          {campaign.scheduled_timezone && TZ_ABBR[campaign.scheduled_timezone] && (
                            <span className="ml-1 text-warm-400">{TZ_ABBR[campaign.scheduled_timezone]}</span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <span className="text-warm-500">{formatDate(campaign.created_at)}</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <button
                    onClick={() => navigate(`/admin/campaigns/${campaign.id}`)}
                    className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    {campaign.status === 'draft' ? 'Edit' : 'View'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {campaigns.map((campaign) => (
          <button
            key={campaign.id}
            onClick={() => navigate(`/admin/campaigns/${campaign.id}`)}
            className="block w-full text-left bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300"
          >
            <p className="font-medium text-warm-900 mb-1">{campaign.name}</p>
            <div className="flex items-center gap-2 mb-2">
              {getTypeBadge(campaign.campaign_type)}
              {getStatusBadge(campaign.status)}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-warm-600">
                {campaign.completed_at
                  ? `Sent ${formatDate(campaign.completed_at)}`
                  : campaign.scheduled_for
                    ? `Scheduled ${formatDate(campaign.scheduled_for)}`
                    : formatDate(campaign.created_at)}
              </span>
              {(campaign.sent_count || campaign.email_sends?.[0]?.count || 0) > 0 && (
                <span className="text-warm-500">
                  {campaign.sent_count || campaign.email_sends?.[0]?.count} recipients
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
          <p className="text-sm text-warm-600">
            Showing {((page - 1) * PAGE_SIZE) + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} campaigns
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-warm-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}
